import React, { useEffect, useState, useCallback } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { pushSupported, subscribeToPush, unsubscribeFromPush, currentSubscription } from "@/lib/push";
import api from "@/lib/api";

const isIOS = typeof navigator !== "undefined" && /iP(hone|ad|od)/.test(navigator.userAgent);
const isStandalone = typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);

export default function PushToggle() {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState("default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    if (!pushSupported()) { setSupported(false); setReady(true); return; }
    setPermission(Notification.permission);
    const s = await currentSubscription();
    setSubscribed(!!s);
    setReady(true);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const toggle = async (on) => {
    setBusy(true);
    try {
      if (on) {
        await subscribeToPush();
        setSubscribed(true);
        setPermission("granted");
        toast.success("Push notifications turned on");
      } else {
        await unsubscribeFromPush();
        setSubscribed(false);
        toast.success("Push notifications turned off");
      }
    } catch (e) {
      if (e.message === "denied") {
        setPermission("denied");
        toast.error("Notifications are blocked in your browser settings");
      } else if (e.message === "unsupported") {
        setSupported(false);
      } else {
        toast.error(on ? "Could not turn on notifications" : "Could not turn off notifications");
      }
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/push/test");
      toast.success(`Test sent to ${data.devices} device${data.devices === 1 ? "" : "s"} — check your notifications`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send test notification");
    } finally {
      setTesting(false);
    }
  };

  if (!ready) return null;

  const blocked = permission === "denied";
  const iosNeedsInstall = !supported && isIOS && !isStandalone;
  const iosTooOld = !supported && isIOS && isStandalone;

  let status;
  if (!supported) {
    if (iosNeedsInstall) status = "Add RallyPointGo to your Home Screen to enable alerts";
    else if (iosTooOld) status = "Requires iPadOS / iOS 16.4 or later for push";
    else status = "Not supported on this browser";
  } else if (blocked) status = "Blocked in your browser settings";
  else if (subscribed) status = "On — you'll get alerts even when the app is closed";
  else status = "Off — turn on to get alerts for events, conferences and announcements";

  return (
    <Card className="mb-5 border-slate-200 p-4 shadow-sm" data-testid="push-toggle">
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${subscribed ? "bg-sky-100 text-sky-600" : "bg-slate-100 text-slate-500"}`}>
          {subscribed ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-800">Push notifications</p>
          <p className="mt-0.5 text-sm text-slate-500" data-testid="push-toggle-status">{status}</p>
        </div>
        {supported && !blocked && (
          <div className="flex items-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            <Switch checked={subscribed} disabled={busy} onCheckedChange={toggle} data-testid="push-toggle-switch" />
          </div>
        )}
      </div>

      {subscribed && !blocked && (
        <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
          <Button variant="outline" size="sm" className="rounded-full" onClick={sendTest} disabled={testing} data-testid="push-test-btn">
            {testing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Bell className="mr-1.5 h-3.5 w-3.5" />} Send test to my devices
          </Button>
          <span className="text-xs text-slate-400">Confirms push is working on each signed-in device</span>
        </div>
      )}

      {blocked && (
        <p className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-800" data-testid="push-toggle-blocked-help">
          You previously blocked notifications. To turn them back on, click the <strong>lock icon</strong> (or site settings) in your browser's address bar, set <strong>Notifications</strong> to <strong>Allow</strong>, then reload this page and toggle it on here.
        </p>
      )}
      {iosNeedsInstall && (
        <p className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-600" data-testid="push-toggle-ios-help">
          On iPhone/iPad, open RallyPointGo in <strong>Safari</strong>, tap <strong>Share</strong> → <strong>Add to Home Screen</strong>, then open it from that icon and turn notifications on here.
        </p>
      )}
      {iosTooOld && (
        <p className="mt-3 rounded-lg bg-amber-50 p-3 text-xs text-amber-800" data-testid="push-toggle-ios-old-help">
          This device's iPadOS/iOS version doesn't support push notifications. Apple added web push in <strong>iPadOS/iOS 16.4</strong>. Update via <strong>Settings → General → Software Update</strong> if available, or use a newer device or a desktop browser. In-app notifications and email alerts still work here.
        </p>
      )}
    </Card>
  );
}
